<template>
  <div v-if="firebaseConfigured" class="my-stats">
    <template v-if="!uid">
      <p class="my-stats__signin muted">
        Sign in (Play online) to keep a record of every game you finish.
      </p>
    </template>

    <p v-else-if="loading" class="muted">Looking up your record&hellip;</p>

    <p v-else-if="!summary" class="muted">
      No finished games on record yet — your next one starts the count.
      <!-- If a save was attempted this session and failed, say so. An empty
           record and a refused write read identically otherwise, which is
           exactly how this went unnoticed across the hub. -->
      <span v-if="saveError" class="my-stats__error">Last save failed — {{ saveError }}</span>
    </p>

    <template v-else>
      <button
        type="button"
        class="my-stats__head"
        :aria-expanded="open ? 'true' : 'false'"
        @click="open = !open"
      >
        <span class="my-stats__headline">
          <strong>{{ summary.gamesPlayed }}</strong> {{ summary.gamesPlayed === 1 ? 'game' : 'games' }},
          <strong>{{ summary.wins || 0 }}</strong> won
        </span>
        <span class="my-stats__rate">{{ winPercent }}</span>
      </button>

      <dl v-if="open" class="my-stats__grid">
        <div v-for="row in rows" :key="row.label">
          <dt>{{ row.label }}</dt>
          <dd>{{ row.value }}</dd>
        </div>
      </dl>
    </template>
  </div>
</template>

<script>
// "It would be fun for all of the games that we've made if they could keep
// track of my stats per game" (bug report, 2026-08-29). The recording half
// is store/userStats.js; this reads the summary back.
//
// House component: the same panel, same collapsed-by-default shape, in every
// game - only `rows` differs, because only the per-game counters differ.
// Headline first (games and wins are what anyone actually wants), everything
// else behind the tap.
//
// Read once on mount rather than bound live: a lifetime record changes at
// most once per game, and this sits on the menu you pass through on the way
// into the next one.
import { mapState } from 'vuex';
import { readMySummary, winRate, statsDiagnostics } from '@/store/userStats';

export default {
  name: 'MyStatsPanel',
  data() {
    return {
      loading: true, summary: null, open: false, saveError: null,
    };
  },
  computed: {
    ...mapState(['firebaseConfigured']),
    uid() { return this.$store.getters.myUid; },
    winPercent() {
      const rate = winRate(this.summary);
      return rate === null ? '' : `${Math.round(rate * 100)}%`;
    },
    rows() {
      const s = this.summary;
      return [
        { label: 'Won', value: s.wins || 0 },
        { label: 'Lost', value: s.losses || 0 },
        { label: 'Online', value: s.onlineGames || 0 },
        { label: 'Columns claimed', value: s.totals?.columnsClaimed || 0 },
      ];
    },
  },
  watch: {
    // The menu renders before auth settles, so the uid usually arrives after
    // this component is already on screen.
    uid: { handler: 'load', immediate: true },
  },
  methods: {
    async load() {
      if (!this.uid) { this.loading = false; return; }
      this.loading = true;
      this.summary = await readMySummary(this.uid);
      this.saveError = statsDiagnostics.lastError;
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.my-stats {
  border: 1px solid var(--edge);
  border-radius: var(--radius);
  background: var(--surface);
  padding: 0.7rem 0.85rem;
}

.my-stats__signin { font-size: 0.82rem; }

// Deliberately legible rather than shouty: this is a diagnostic, not an
// error the player caused or can fix. --danger on the card background.
.my-stats__error {
  color: var(--danger);
  display: block;
  font-size: 0.75rem;
  margin-top: 0.4rem;
  overflow-wrap: anywhere;
}

.my-stats__head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  min-height: 0;
  padding: 0;
  text-align: left;
  width: 100%;
}

.my-stats__headline { color: var(--ink); font-size: 0.9rem; }
.my-stats__rate { color: var(--accent); font-weight: 800; font-size: 0.95rem; }

.my-stats__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem 0.9rem;
  margin: 0.7rem 0 0;
  padding-top: 0.6rem;
  border-top: 1px solid var(--edge);

  > div { display: flex; justify-content: space-between; gap: 0.5rem; }
  dt { color: var(--ink-dim); font-size: 0.8rem; }
  dd { color: var(--ink); font-size: 0.8rem; font-weight: 700; margin: 0; }
}
</style>
